
import React from "react";
import { Student } from "@/services/db";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { User, Phone, Mail } from "lucide-react";

interface StudentInfoProps {
  student: Student;
}

const StudentInfo: React.FC<StudentInfoProps> = ({ student }) => {
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const totalPrograms = student.programs?.length || 0;
  const completedPrograms = student.programs?.filter(p => p.feeStatus === 'paid')?.length || 0;

  return (
    <div className="space-y-1.5">
      {/* Profile Section */}
      <div className="flex items-center space-x-2">
        <Avatar className="h-8 w-8">
          <AvatarImage 
            src={student.profileImage} 
            alt={student.name}
          />
          <AvatarFallback className="bg-nalc-teal text-white text-xs font-semibold">
            {getInitials(student.name)}
          </AvatarFallback>
        </Avatar>
        
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
            {student.name}
          </h3>
          
          {student.surname && (
            <p className="text-xs text-slate-600 dark:text-slate-400 truncate">
              {student.surname}
            </p>
          )}
          
          <div className="flex items-center gap-1 mt-0.5">
            <Badge variant="outline" className="text-xs py-0 px-1 h-4">
              ID: #{student.id}
            </Badge>
            
            {student.age && (
              <Badge variant="secondary" className="text-xs py-0 px-1 h-4">
                Age: {student.age}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="space-y-0.5">
        {student.contactNumber && (
          <div className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
            <Phone className="h-2.5 w-2.5" />
            <span className="truncate">{student.contactNumber}</span>
          </div>
        )}
        
        {student.email && (
          <div className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
            <Mail className="h-2.5 w-2.5" />
            <span className="truncate">{student.email}</span>
          </div>
        )}
        
        {student.address && (
          <div className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
            <User className="h-2.5 w-2.5" />
            <span className="truncate">{student.address}</span>
          </div>
        )}
      </div>

      {/* Program Summary */}
      <div className="bg-slate-50 dark:bg-slate-700/50 rounded-md p-1.5">
        <div className="flex justify-between items-center">
          <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
            Programs Progress
          </span>
          <div className="text-right">
            <div className="text-xs font-bold text-nalc-teal">
              {completedPrograms}/{totalPrograms}
            </div>
            <div className="text-xs text-slate-500">
              Completed
            </div>
          </div>
        </div>
        
        {totalPrograms > 0 && (
          <div className="mt-1 bg-slate-200 dark:bg-slate-600 rounded-full h-1">
            <div 
              className="bg-nalc-teal rounded-full h-1 transition-all duration-300"
              style={{ width: `${(completedPrograms / totalPrograms) * 100}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentInfo;
